import React from 'react';
import icons from '@sizeid/assets/icons/selection.json';
import logos from '@sizeid/assets/logos/selection.json';
import pictograms from '@sizeid/assets/pictograms/selection.json';
import flags from '@sizeid/assets/flags/selection.json';

import { SvgImageProps, SvgImageVariantProps } from './types';
import Svg from './styled/Svg';

// Inspired by https://github.com/aykutkardas/react-icomoon/blob/master/src/index.tsx

type Icon = {
  paths: string[];
  attrs?: Record<string, unknown>[];
  width?: number | string;
};

type IconSetItem = {
  properties: {
    name: string;
  };
  icon: Icon;
};

type IconSetA = {
  icons: IconSetItem[];
};

type IconSetB = { selection: { name: string }[]; icons: Icon[] };

// Because there are 2 icomoon file formats
type IconSet = IconSetA | IconSetB;

const isIconSetA = (set: IconSet): set is IconSetA =>
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  set.selection === undefined;

const isIconSetItem = (
  iconSetItem: IconSetItem | Icon,
): iconSetItem is IconSetItem =>
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  iconSetItem.icon;

const SvgImage: React.FunctionComponent<
  {
    iconSet: IconSet;
    image: string;
  } & SvgImageProps
> = ({ iconSet, image, ...props }) => {
  if (!iconSet || !image) return null;

  let currentIcon: IconSetItem | Icon | undefined;
  if (!isIconSetA(iconSet)) {
    currentIcon =
      iconSet.icons[
        iconSet.selection.findIndex(selection => selection.name === image) ?? -1
      ];
  } else {
    currentIcon = iconSet.icons.find(item => item.properties.name === image);
  }

  if (!currentIcon) return null;

  const icon: Icon = isIconSetItem(currentIcon)
    ? currentIcon.icon
    : currentIcon;

  const viewBox = `0 0 ${icon.width ?? 1024} 1024`;
  const childs = icon.paths.map((path, index) => {
    const pathProps = {
      d: path,
      key: image + index,
      ...(icon.attrs ? icon.attrs[index] : {}),
    };
    return <path {...pathProps} />;
  });

  return (
    <Svg
      className={`${props.className} ${props.className}-${image}`}
      viewBox={viewBox}
      data-test-id={`${props.className}-${image}`}
      {...props}
    >
      {childs}
    </Svg>
  );
};

export const Icon: React.FC<SvgImageVariantProps> = props => (
  <SvgImage className="icon" iconSet={icons} {...props} />
);

export const Logo: React.FC<SvgImageVariantProps> = props => (
  <SvgImage className="logo" iconSet={logos} {...props} />
);

export const Pictogram: React.FC<SvgImageVariantProps> = props => (
  <SvgImage className="pictogram" iconSet={pictograms} {...props} />
);

export const Flag: React.FC<SvgImageVariantProps> = props => (
  <SvgImage className="flag" iconSet={flags} {...props} />
);

export default SvgImage;
